import * as React from "react";
import { cn } from "@/lib/utils";

interface DashboardCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  /** Use destructive border when over budget or in error state */
  variant?: "default" | "destructive";
  /** Remove padding for custom layouts */
  noPadding?: boolean;
}

const DashboardCard = React.forwardRef<HTMLDivElement, DashboardCardProps>(
  ({ className, children, variant = "default", noPadding = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-2xl border-2 bg-card text-card-foreground overflow-hidden",
          variant === "destructive" ? "border-destructive/30" : "border-primary/20",
          !noPadding && "p-6",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DashboardCard.displayName = "DashboardCard";

interface DashboardCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DashboardCardHeader = React.forwardRef<HTMLDivElement, DashboardCardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center justify-between", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DashboardCardHeader.displayName = "DashboardCardHeader";

interface DashboardCardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
  icon?: React.ReactNode;
}

const DashboardCardTitle = React.forwardRef<HTMLHeadingElement, DashboardCardTitleProps>(
  ({ className, children, icon, ...props }, ref) => {
    return (
      <div className="flex items-center gap-2">
        {icon}
        <h3
          ref={ref}
          className={cn("text-heading-section", className)}
          {...props}
        >
          {children}
        </h3>
      </div>
    );
  }
);
DashboardCardTitle.displayName = "DashboardCardTitle";

interface DashboardCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DashboardCardContent = React.forwardRef<HTMLDivElement, DashboardCardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DashboardCardContent.displayName = "DashboardCardContent";

export { DashboardCard, DashboardCardHeader, DashboardCardTitle, DashboardCardContent };
